import { AxiosError } from "axios";
import { HttpClient } from "../../../../shared/infrastructure/services";
import { ServerErrorResponse } from "../../../../shared/domain/entities";
import { logout as oldLogout } from "../../../../old/redux/auth/authActions";
import { toast } from "react-hot-toast";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { createContext, useContext, useMemo } from "react";
import { logout, useAuth } from "./auth-context";

type FetchContextT = {
  httpClient: typeof HttpClient | null;
  activeSession: boolean | null;
};

const FetchContext = createContext<FetchContextT>({
  httpClient: null,
  activeSession: null,
});

if (process.env.NODE_ENV === "development") {
  FetchContext.displayName = "FetchContext";
}

type FetchProviderProps = {
  children?: React.ReactNode;
};

const FetchProvider = ({
  children,
}: FetchProviderProps): React.ReactElement => {
  const [activeSession, setActiveSession] = useState(false);
  const { user, setData } = useAuth();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const timezone = useMemo(() => {
    const offset =
      user?.timezone?.offset || -new Date().getTimezoneOffset() / 60;
    const minuteSide = offset % 1 !== 0 ? Math.abs(offset % 1) * 60 : 0;
    const hourSide = offset % 1 !== 0 ? Math.floor(offset) : offset;

    return `${hourSide >= 0 ? "+" : "-"}${
      Math.abs(hourSide) > 9 ? Math.abs(hourSide) : `0${Math.abs(hourSide)}`
    }:${minuteSide || "00"}`;
  }, [user?.timezone]);

  useEffect(() => {
    const token = user?.access_token;
    const reqInterceptor = HttpClient.interceptors.request.use(
      (config) => ({
        ...config,
        headers: token
          ? {
              Authorization: `Bearer ${token ?? ""}`,
              "x-timezone": timezone,
            }
          : undefined,
      }),
      (error) => Promise.reject(error)
    );
    setActiveSession(!!user?.access_token);

    return () => {
      HttpClient.interceptors.request.eject(reqInterceptor);
    };
  }, [user, timezone]);

  useEffect(() => {
    const resInterceptor = HttpClient.interceptors.response.use(
      (response) => {
        return response;
      },
      (error: AxiosError<ServerErrorResponse>) => {
        setActiveSession((prevValue) => {
          if (error?.response?.data?.code === "TOKEN_EXPIRED") {
            logout(setData, queryClient);
            // TODO: - Stop using when the project is migrated.
            dispatch(oldLogout());
            prevValue &&
              toast.error("Your session expired. Please login again.");

            return false;
          }

          return prevValue;
        });

        return Promise.reject(error);
      }
    );

    return () => {
      HttpClient.interceptors.request.eject(resInterceptor);
    };
  }, [dispatch, queryClient, setData]);

  return (
    <FetchContext.Provider value={{ httpClient: HttpClient, activeSession }}>
      {children}
    </FetchContext.Provider>
  );
};

const useFetch = (): typeof HttpClient => {
  const { httpClient } = useContext(FetchContext);

  if (httpClient === null) {
    throw new Error("useFetch must be used within a FetchProvider");
  }

  return httpClient;
};

const useActiveSession = (): boolean => {
  const { activeSession } = useContext(FetchContext);

  if (activeSession === null) {
    throw new Error("useActiveSession must be used within a FetchProvider");
  }

  return activeSession;
};

export { FetchProvider, useActiveSession, useFetch };
